import { STORAGE_KEYS, RETENTION_POLICIES } from '@/constants/storage';
import StorageService from '@/services/StorageService';

/**
 * CameraService - Handles camera-related business logic
 */
class CameraService {
  /**
   * Set multi-cam layout configuration
   * @param {number} layout - Number of columns for multicam layout
   */
  setMulticamLayout(layout) {
    if (typeof layout !== 'number' || layout < 1) {
      return;
    }

    StorageService.setItem(
      STORAGE_KEYS.MULTICAM_LAYOUT,
      layout,
      null, // global key, not avatar-scoped
      RETENTION_POLICIES.AVATAR_SCOPED_E
    );
  }

  /**
   * Get multi-cam layout configuration
   * @returns {number} Number of columns for multicam layout
   */
  getMulticamLayout() {
    return StorageService.getItem(
      STORAGE_KEYS.MULTICAM_LAYOUT,
      null, // global key, not avatar-scoped
      1 // default to 1 column for large screens
    );
  }

  /**
   * Save camera list order in settings (owner/admin only)
   * @param {Array} cameraList - Camera list order to save
   * @param {string} avatarId - Avatar ID for scoping
   * @param {boolean} isOwner - Whether the current avatar is an owner/admin
   */
  saveCameraListSetting(cameraList, avatarId, isOwner = false) {
    if (!avatarId || !isOwner || !Array.isArray(cameraList)) {
      return;
    }

    StorageService.setItem(
      STORAGE_KEYS.CAMERA_LIST_SETTING,
      cameraList,
      avatarId,
      RETENTION_POLICIES.MULTI_AVATAR_PERSISTENT
    );
  }

  /**
   * Get camera list order in settings (owner/admin only)
   * @param {string} avatarId - Avatar ID for scoping
   * @param {boolean} isOwner - Whether the current avatar is an owner/admin
   * @returns {Array} Camera list order
   */
  getCameraListSetting(avatarId, isOwner = false) {
    if (!avatarId || !isOwner) {
      return [];
    }

    return StorageService.getItem(
      STORAGE_KEYS.CAMERA_LIST_SETTING,
      avatarId,
      []
    );
  }

  /**
   * Save selected home group for avatar
   * @param {any} homeGroup - Home group data to save
   * @param {string} avatarId - Avatar ID for scoping
   */
  saveSelectedHomeGroup(homeGroup, avatarId) {
    if (!avatarId) {
      return;
    }

    StorageService.setItem(
      STORAGE_KEYS.SELECTED_HOME_GROUP,
      homeGroup,
      avatarId,
      RETENTION_POLICIES.SESSION_PERSISTENT
    );
  }

  /**
   * Get selected home group for avatar
   * @param {string} avatarId - Avatar ID for scoping
   * @returns {any} Selected home group data
   */
  getSelectedHomeGroup(avatarId) {
    if (!avatarId) {
      return null;
    }

    return StorageService.getItem(
      STORAGE_KEYS.SELECTED_HOME_GROUP,
      avatarId,
      null // default to null (no group selected)
    );
  }

  /**
   * Save clip search parameters
   * @param {Object} searchParams - Search parameters to save
   */
  saveClipSearchParams(searchParams) {
    if (!searchParams || typeof searchParams !== 'object') {
      return;
    }

    StorageService.setItem(
      STORAGE_KEYS.CLIP_SEARCH_PARAMS,
      searchParams,
      null,
      RETENTION_POLICIES.AVATAR_SCOPED_E
    );
  }

  /**
   * Get clip search parameters
   * @returns {Object} Search parameters
   */
  getClipSearchParams() {
    return StorageService.getItem(STORAGE_KEYS.CLIP_SEARCH_PARAMS, null, {});
  }

  /**
   * Save clip list position offset
   * @param {number} listOffset - Position offset to save
   */
  saveClipListPosition(listOffset) {
    if (typeof listOffset !== 'number') {
      return;
    }

    StorageService.setItem(
      STORAGE_KEYS.CLIP_LIST_POSITION,
      listOffset,
      null,
      RETENTION_POLICIES.AVATAR_SCOPED_E
    );
  }

  /**
   * Get clip list position offset
   * @returns {number} Position offset
   */
  getClipListPosition() {
    return StorageService.getItem(STORAGE_KEYS.CLIP_LIST_POSITION, null, 0);
  }
}

export default new CameraService();
